import { createContext } from 'react';
import type { PaletteActionDescriptor, PaletteMode, RecentEntity } from './types';

export interface PaletteContextValue {
    open: boolean;
    rawQuery: string;
    mode: PaletteMode;
    query: string;
    recents: RecentEntity[];
    pageActions: PaletteActionDescriptor[];
    /** Id of the view rendered in place of the main list, or null. */
    activeView: string | null;
    /** Query the active view was opened with ('' when opened from its action row). */
    viewInitialQuery: string;
    setOpen: (open: boolean) => void;
    toggle: () => void;
    setRawQuery: (q: string) => void;
    pushRecent: (entry: Omit<RecentEntity, 'recordedAt'>) => void;
    removeRecent: (type: RecentEntity['type'], id: RecentEntity['id']) => void;
    clearRecents: () => void;
    registerPageActions: (id: symbol, actions: PaletteActionDescriptor[]) => void;
    unregisterPageActions: (id: symbol) => void;
    openView: (id: string, initialQuery?: string) => void;
    closeView: () => void;
}

/**
 * The stable action half of the palette context — only referentially-stable
 * callbacks, so components that just register page actions (every page via
 * `usePaletteActions`) don't re-render on each keystroke when `rawQuery`
 * changes. The volatile state stays on {@link PaletteContext}.
 */
export interface PaletteApiValue {
    setOpen: (open: boolean) => void;
    toggle: () => void;
    setRawQuery: (q: string) => void;
    pushRecent: (entry: Omit<RecentEntity, 'recordedAt'>) => void;
    removeRecent: (type: RecentEntity['type'], id: RecentEntity['id']) => void;
    clearRecents: () => void;
    registerPageActions: (id: symbol, actions: PaletteActionDescriptor[]) => void;
    unregisterPageActions: (id: symbol) => void;
    openView: (id: string, initialQuery?: string) => void;
    closeView: () => void;
}

export const PaletteContext = createContext<PaletteContextValue | null>(null);
export const PaletteApiContext = createContext<PaletteApiValue | null>(null);
